import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Divider,
} from '@mui/material'
import { smbsApi, templatesApi } from '../../services/api'
import { useNotification } from '../../contexts/NotificationContext'
import TemplateSelector from '../templates/TemplateSelector'
import { LocalTemplate } from '../../types/templates'

const MAX_INT = 2147483647

export const ShopifyConfig: React.FC<{ smbId: string }> = ({ smbId }) => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  
  const [abandonedCheckoutEnabled, setAbandonedCheckoutEnabled] = useState(false)
  const [abandonedCheckoutDelayMinutes, setAbandonedCheckoutDelayMinutes] = useState<number | string>(30)
  const [abandonedCheckoutTemplateId, setAbandonedCheckoutTemplateId] = useState<string>('')
  const [selectedAbandonedCheckoutTemplate, setSelectedAbandonedCheckoutTemplate] = useState<LocalTemplate | null>(null)

  const [isOrderConfirmationEnabled, setIsOrderConfirmationEnabled] = useState(false)
  const [orderConfirmationTemplateId, setOrderConfirmationTemplateId] = useState<string>('')
  const [selectedOrderConfirmationTemplate, setSelectedOrderConfirmationTemplate] = useState<LocalTemplate | null>(null)
  
  const [initialState, setInitialState] = useState<{
    abandonedCheckoutEnabled: boolean;
    abandonedCheckoutDelayMinutes: number;
    abandonedCheckoutTemplateId: string;
    isOrderConfirmationEnabled: boolean;
    orderConfirmationTemplateId: string;
  } | null>(null)

  const { showNotification } = useNotification()

  useEffect(() => {
    const loadData = async () => {
      try {
        const configRes = await smbsApi.getShopifyConfig(smbId).catch(() => null)
        
        if (configRes) {
          const loadedState = {
            abandonedCheckoutEnabled: configRes.abandoned_checkout_is_enabled ?? false,
            abandonedCheckoutDelayMinutes: configRes.abandoned_checkout_delay_minutes ?? 30,
            abandonedCheckoutTemplateId: configRes.abandoned_checkout_template_id || '',
            isOrderConfirmationEnabled: configRes.is_order_confirmation_enabled ?? false,
            orderConfirmationTemplateId: configRes.order_confirmation_template_id || '',
          }
          setAbandonedCheckoutEnabled(loadedState.abandonedCheckoutEnabled)
          setAbandonedCheckoutDelayMinutes(loadedState.abandonedCheckoutDelayMinutes)
          setAbandonedCheckoutTemplateId(loadedState.abandonedCheckoutTemplateId)
          setIsOrderConfirmationEnabled(loadedState.isOrderConfirmationEnabled)
          setOrderConfirmationTemplateId(loadedState.orderConfirmationTemplateId)
          setInitialState(loadedState)
          
          if (loadedState.abandonedCheckoutTemplateId || loadedState.orderConfirmationTemplateId) {
            const res = await templatesApi.getTemplates({ smb_id: smbId }).catch(() => null)
            const tpls = res?.templates || res?.data || []
            
            if (loadedState.abandonedCheckoutTemplateId) {
              const found = tpls.find((t: any) => t.id === loadedState.abandonedCheckoutTemplateId)
              if (found) {
                setSelectedAbandonedCheckoutTemplate(found)
              }
            }
            if (loadedState.orderConfirmationTemplateId) {
              const found = tpls.find((t: any) => t.id === loadedState.orderConfirmationTemplateId)
              if (found) {
                setSelectedOrderConfirmationTemplate(found)
              }
            }
          }
        }
      } catch (err) {
        console.error("Failed to load shopify config", err)
      } finally {
        setLoading(false)
      }
    }
    
    if (smbId) {
      loadData()
    }
  }, [smbId])

  const verifyTemplateStatus = async (tplId: string, currentTemplate: LocalTemplate | null, setTemplate: (tpl: LocalTemplate) => void) => {
    let tplToVerify = currentTemplate
    if (!tplToVerify || tplToVerify.id !== tplId) {
      try {
        const res = await templatesApi.getTemplates({ smb_id: smbId })
        const tpls = res?.templates || res?.data || []
        const found = tpls.find((t: any) => t.id === tplId)
        if (found) {
          tplToVerify = found
          setTemplate(found)
        }
      } catch (err) {
        console.error('Failed to retrieve templates for verification:', err)
      }
    }

    if (tplToVerify && tplToVerify.platform_template_id) {
      try {
        const response = await templatesApi.verifyTemplateStatus(smbId, [tplToVerify.platform_template_id])
        const tplResult = response[tplToVerify.platform_template_id]
        if (!tplResult?.can_proceed) {
          showNotification(tplResult?.message || 'Template is not approved', 'error')
          return false
        }
        return true
      } catch (error) {
        console.error('Failed to verify template status:', error)
        showNotification('Failed to verify template status', 'error')
        return false
      }
    } else {
      showNotification('Selected template details could not be found or verified', 'error')
      return false
    }
  }

  const handleSave = async () => {
    if (abandonedCheckoutEnabled) {
      if (!abandonedCheckoutTemplateId) {
        showNotification('Please select a template when enabling abandoned checkout', 'error')
        return
      }
      if (abandonedCheckoutDelayMinutes === '' || Number(abandonedCheckoutDelayMinutes) < 0 || Number(abandonedCheckoutDelayMinutes) > MAX_INT) {
        showNotification(`Delay minutes must be between 0 and ${MAX_INT.toLocaleString()}`, 'error')
        return
      }
    }

    if (isOrderConfirmationEnabled && !orderConfirmationTemplateId) {
      showNotification('Please select a template when enabling order confirmation', 'error')
      return
    }

    setSaving(true)

    if (abandonedCheckoutEnabled && abandonedCheckoutTemplateId) {
      const isValid = await verifyTemplateStatus(abandonedCheckoutTemplateId, selectedAbandonedCheckoutTemplate, setSelectedAbandonedCheckoutTemplate)
      if (!isValid) {
        setSaving(false)
        return
      }
    }

    if (isOrderConfirmationEnabled && orderConfirmationTemplateId) {
      const isValid = await verifyTemplateStatus(orderConfirmationTemplateId, selectedOrderConfirmationTemplate, setSelectedOrderConfirmationTemplate)
      if (!isValid) {
        setSaving(false)
        return
      }
    }

    try {
      await smbsApi.updateShopifyConfig(smbId, {
        abandoned_checkout_is_enabled: abandonedCheckoutEnabled,
        abandoned_checkout_delay_minutes: Number(abandonedCheckoutDelayMinutes),
        abandoned_checkout_template_id: abandonedCheckoutTemplateId || null,
        is_order_confirmation_enabled: isOrderConfirmationEnabled,
        order_confirmation_template_id: orderConfirmationTemplateId || null,
      })
      showNotification('Shopify configuration saved', 'success')
      setInitialState({
        abandonedCheckoutEnabled,
        abandonedCheckoutDelayMinutes: Number(abandonedCheckoutDelayMinutes),
        abandonedCheckoutTemplateId,
        isOrderConfirmationEnabled,
        orderConfirmationTemplateId,
      })
    } catch (err: any) {
      showNotification(err.response?.data?.detail || 'Failed to save configuration', 'error')
    } finally {
      setSaving(false)
    }
  }

  const hasChanges = initialState ? (
    abandonedCheckoutEnabled !== initialState.abandonedCheckoutEnabled ||
    Number(abandonedCheckoutDelayMinutes) !== initialState.abandonedCheckoutDelayMinutes ||
    abandonedCheckoutTemplateId !== initialState.abandonedCheckoutTemplateId ||
    isOrderConfirmationEnabled !== initialState.isOrderConfirmationEnabled ||
    orderConfirmationTemplateId !== initialState.orderConfirmationTemplateId
  ) : false

  const isSaveDisabled = saving || (abandonedCheckoutEnabled && !abandonedCheckoutTemplateId) || (isOrderConfirmationEnabled && !orderConfirmationTemplateId) || !hasChanges

  if (loading) {
    return (
      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">Loading shopify config...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
      <Typography variant="subtitle2" gutterBottom>
        Shopify Configuration
      </Typography>
      <Typography variant='body2' color="text.secondary" sx={{ mb: 2 }}>
        Manage your Shopify integration settings and automated messages.
      </Typography>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" gutterBottom>
        Abandoned Checkout Message
      </Typography>
      <Typography variant='body2' color="text.secondary">
        Send a WhatsApp message to customers who didn’t finish checkout.
      </Typography>
      <Stack spacing={2} sx={{ mt: 1, mb: 3 }}>
        <FormControlLabel
          control={<Switch size="small" checked={abandonedCheckoutEnabled} onChange={(e) => setAbandonedCheckoutEnabled(e.target.checked)} />}
          label={<Typography variant="body2">Enable</Typography>}
        />
        
        <TextField
          label="Send after X minutes of inactivity"
          type="number"
          size="small"
          value={abandonedCheckoutDelayMinutes}
          onChange={(e) => {
            const val = e.target.value
            if (val === '') {
              setAbandonedCheckoutDelayMinutes('')
              return
            }
            const num = Number(val)
            if (!isNaN(num) && num >= 0 && num <= MAX_INT) {
              setAbandonedCheckoutDelayMinutes(val)
            }
          }}
          inputProps={{ min: 0, max: MAX_INT }}
          disabled={!abandonedCheckoutEnabled}
          sx={{ opacity: !abandonedCheckoutEnabled ? 0.6 : 1 }}
        />
        
        <TemplateSelector 
          smbId={smbId}
          value={abandonedCheckoutTemplateId}
          label="WhatsApp Template"
          disabled={!abandonedCheckoutEnabled}
          onSelect={(tpl) => {
            setAbandonedCheckoutTemplateId(tpl?.id || '')
            setSelectedAbandonedCheckoutTemplate(tpl)
          }}
        />
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" gutterBottom>
        Order Confirmation Message
      </Typography>
      <Typography variant='body2' color="text.secondary">
        Automatically send a WhatsApp message to customers when they successfully complete an order.
      </Typography>
      <Stack spacing={2} sx={{ mt: 1, mb: 3 }}>
        <FormControlLabel
          control={<Switch size="small" checked={isOrderConfirmationEnabled} onChange={(e) => setIsOrderConfirmationEnabled(e.target.checked)} />}
          label={<Typography variant="body2">Enable</Typography>}
        />
        <TemplateSelector 
          smbId={smbId}
          value={orderConfirmationTemplateId}
          label="Order Confirmation Template"
          disabled={!isOrderConfirmationEnabled}
          onSelect={(tpl) => {
            setOrderConfirmationTemplateId(tpl?.id || '')
            setSelectedOrderConfirmationTemplate(tpl)
          }}
        />
      </Stack>

      <Button 
        variant="contained" 
        size="small" 
        onClick={handleSave} 
        disabled={isSaveDisabled}
        sx={{ alignSelf: 'flex-start' }}
      >
        {saving ? <CircularProgress size={16} /> : 'Save Config'}
      </Button>
    </Box>
  )
}
